import React, { useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckIcon from "@mui/icons-material/Check";
import { useMutation } from "@tanstack/react-query";

import ModernModal from "../../../common/ModernModal";
import { ModernFormAlert, ModernFormField } from "../../../common/ModernForm";
import { audiogramsService, CustomAudiogramSuggestion } from "../../services";
import AudioTrimmer from "../AudioTrimmer";

type Step = "input" | "suggested" | "preview";

interface GenerateCustomAudiogramModalProps {
  open: boolean;
  episodeId: string;
  audioUrl?: string;
  hasCredits: boolean;
  onClose: () => void;
  onSuccessfullySaved: () => void;
  onCreditsExhausted: () => void;
}

export default function GenerateCustomAudiogramModal({
  open,
  episodeId,
  audioUrl = "",
  hasCredits,
  onClose,
  onSuccessfullySaved,
  onCreditsExhausted,
}: GenerateCustomAudiogramModalProps) {
  const [step, setStep] = useState<Step>("input");
  const [prompt, setPrompt] = useState("");
  const [suggestions, setSuggestions] = useState<
    CustomAudiogramSuggestion[] | null
  >(null);
  const [selected, setSelected] = useState<CustomAudiogramSuggestion | null>(
    null,
  );
  const [trimmedStart, setTrimmedStart] = useState(0);
  const [trimmedEnd, setTrimmedEnd] = useState(0);
  const [error, setError] = useState("");

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const reset = () => {
    setStep("input");
    setPrompt("");
    setSuggestions(null);
    setSelected(null);
    setTrimmedStart(0);
    setTrimmedEnd(0);
    setError("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const ideaMutation = useMutation({
    mutationKey: ["audiogram-ideas", episodeId],
    mutationFn: ({
      p,
      allowAdditionalCharges,
    }: {
      p: string;
      allowAdditionalCharges?: boolean;
    }) =>
      audiogramsService.generateCustomAudiogramIdeas(
        episodeId,
        p,
        allowAdditionalCharges,
      ),
    onSuccess: (data) => {
      setSuggestions(data);
      setStep("suggested");
      setError("");
    },
    onError: (err: any) => {
      if (err?.statusCode === 429) {
        onCreditsExhausted();
        handleClose();
      } else {
        setError(
          err?.response?.data?.message ||
            "Failed to generate ideas. Please try again.",
        );
      }
    },
  });

  const saveMutation = useMutation({
    mutationKey: ["save-custom-audiogram"],
    mutationFn: (suggestion: CustomAudiogramSuggestion) =>
      audiogramsService.saveCustomAudiogram(episodeId, suggestion),
    onSuccess: () => {
      onSuccessfullySaved();
      reset();
      onClose();
    },
    onError: (err: any) => {
      setError(
        err?.response?.data?.message ||
          "Failed to generate audiogram. Please try again.",
      );
    },
  });

  const handleSuggestionSelect = (suggestion: CustomAudiogramSuggestion) => {
    setSelected(suggestion);
    setTrimmedStart(suggestion.start);
    setTrimmedEnd(suggestion.end);
    setStep("preview");
  };

  const handleFinalSubmit = () => {
    if (!selected) return;
    setError("");
    saveMutation.mutate({
      ...selected,
      start: trimmedStart,
      end: trimmedEnd,
    });
  };

  const isLoading = ideaMutation.isPending || saveMutation.isPending;

  const getModalTitle = () => {
    if (step === "input") return "Generate Custom Audiogram";
    if (step === "suggested") return "Choose Audiogram";
    return `Preview & Edit: ${selected?.title || ""}`;
  };

  const actions = (() => {
    if (step === "input") {
      return (
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setError("");
              ideaMutation.mutate({ p: prompt });
            }}
            disabled={!prompt.trim() || isLoading || !hasCredits}
            startIcon={
              ideaMutation.isPending ? (
                <CircularProgress
                  size={20}
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                />
              ) : null
            }
            sx={{
              background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
              boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                boxShadow: "0 6px 20px rgba(139, 92, 246, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                background: "#e2e8f0 !important",
                color: "#64748b !important",
                boxShadow: "none !important",
                transform: "none",
                border: "1px solid #cbd5e1 !important",
              },
            }}
          >
            {ideaMutation.isPending
              ? "Generating..."
              : "Generate Audiogram Ideas"}
          </Button>
        </Box>
      );
    }

    if (step === "suggested") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button
            onClick={handleClose}
            disabled={isLoading}
            sx={{ color: "#ef4444", border: "1px solid #ef4444" }}
          >
            Discard Draft
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setStep("input");
              ideaMutation.reset();
              setError("");
            }}
            disabled={isLoading}
          >
            Back to Edit Prompt
          </Button>
        </Box>
      );
    }

    // preview step
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => setStep("suggested")}
          disabled={isLoading}
        >
          Back to Options
        </Button>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="outlined" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleFinalSubmit}
            disabled={isLoading}
            startIcon={
              saveMutation.isPending ? (
                <CircularProgress
                  size={20}
                  sx={{ color: "rgba(255,255,255,0.8)" }}
                />
              ) : (
                <CheckIcon />
              )
            }
            sx={{
              background: "linear-gradient(135deg, #3b82f6, #2563eb)",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                boxShadow: "0 6px 20px rgba(59, 130, 246, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                background: "#e2e8f0 !important",
                color: "#64748b !important",
                boxShadow: "none !important",
                transform: "none",
                border: "1px solid #cbd5e1 !important",
              },
            }}
          >
            {saveMutation.isPending ? "Saving..." : "Save Audiogram"}
          </Button>
        </Box>
      </Box>
    );
  })();

  return (
    <ModernModal
      open={open}
      onClose={handleClose}
      title={getModalTitle()}
      maxWidth="lg"
      actions={actions}
      disableBackdropClick={isLoading}
      disableEscapeKeyDown={isLoading}
      disableCloseButton={isLoading}
    >
      {error && (
        <Box sx={{ mb: 2 }}>
          <ModernFormAlert severity="error">{error}</ModernFormAlert>
        </Box>
      )}

      {!hasCredits && (
        <Box sx={{ mb: 2 }}>
          <ModernFormAlert severity="error">
            No credits available. Please upgrade your plan to generate
            audiograms.
          </ModernFormAlert>
        </Box>
      )}

      {/* Step 1 — Prompt input */}
      {step === "input" && (
        <Box>
          <Typography
            variant="body1"
            sx={{ mb: 3, color: "#6b7280", lineHeight: 1.6 }}
          >
            Describe the type of audiogram you want to generate from this
            episode:
          </Typography>
          <ModernFormField
            label="Audiogram Description"
            name="prompt"
            multiline
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g., Find the most impactful quote from this episode, highlight a key insight about marketing..."
          />
          {ideaMutation.isPending && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, fontStyle: "italic" }}
            >
              🎵 Analyzing your episode and generating audiogram ideas...
            </Typography>
          )}
        </Box>
      )}

      {/* Step 2 — Choose from suggestions */}
      {step === "suggested" && (
        <Box>
          <Typography
            variant="body1"
            sx={{ mb: 3, color: "#6b7280", lineHeight: 1.6 }}
          >
            Select one of the generated audiogram concepts:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxHeight: "60vh",
              overflowY: "auto",
              px: 1,
            }}
          >
            {suggestions?.map((suggestion, index) => (
              <Card
                key={suggestion.title}
                sx={{
                  cursor: "pointer",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateX(4px)",
                    boxShadow: "0 4px 16px rgba(25, 118, 210, 0.15)",
                    borderColor: "#1976d2",
                  },
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  borderRadius: 2,
                }}
                onClick={() => handleSuggestionSelect(suggestion)}
              >
                <CardContent sx={{ py: 2.5, px: 2.5 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Box
                      sx={{
                        minWidth: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #1976d2, #1565c0)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 1, fontSize: "1rem" }}
                      >
                        {suggestion.title}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Start:</strong> {formatTime(suggestion.start)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>End:</strong> {formatTime(suggestion.end)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Duration:</strong>{" "}
                          {formatTime(suggestion.end - suggestion.start)}
                        </Typography>
                      </Box>
                    </Box>
                    <PlayArrowIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Step 3 — Preview & trim */}
      {step === "preview" && selected && (
        <Box>
          <Box
            sx={{ mb: 3, p: 2, backgroundColor: "#f8fafc", borderRadius: 2 }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Selected Audiogram Concept:
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {selected.title}
            </Typography>
          </Box>

          <AudioTrimmer
            audioUrl={audioUrl}
            initialStart={selected.start}
            initialEnd={selected.end}
            onTimeChange={(start, end) => {
              setTrimmedStart(start);
              setTrimmedEnd(end);
            }}
          />
        </Box>
      )}
    </ModernModal>
  );
}
